import type { FC, ReactNode } from 'react'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useHomeState } from '@/hooks/use-home-state'
import { homeStore } from '@/lib/cloud/store'
import { beginIntro, introEngineFailed, introEngineReady } from '@/lib/intro'
import { IntroCounter } from '@/components/home/intro-counter'
import { SceneBar } from '@/components/home/scene-bar'
import { SceneCopy } from '@/components/home/scene-copy'
import { SceneHud } from '@/components/home/scene-hud'
import { SceneSlider } from '@/components/home/scene-slider'

import { HOME_SCENES } from '@/constants/scenes'

interface EngineHandle {
    detach: () => void
    setVisible: (visible: boolean) => void
}

export const HomeExperience: FC = (): ReactNode => {
    const state = useHomeState()
    const rootRef = useRef<HTMLElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLDivElement>(null)
    const crosshairRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const engineRef = useRef<EngineHandle | null>(null)
    const visibleRef = useRef(true)
    const [barHidden, setBarHidden] = useState(false)
    const scene = HOME_SCENES[state.scene]
    const copy = HOME_SCENES[state.copyScene]

    // Stage the opening before the first paint of this mount, so the finished hero never flashes.
    useLayoutEffect(() => beginIntro(), [])

    useEffect(() => {
        let cancelled = false

        if (homeStore.get().engine !== 'failed') homeStore.set({ engine: 'loading' })

        import('@/lib/cloud/engine')
            .then(({ getHomeEngine }) => {
                const stage = stageRef.current
                const labels = labelRef.current

                if (cancelled || !stage || !labels) return

                const engine = getHomeEngine()

                if (!engine) {
                    homeStore.set({ engine: 'failed' })
                    introEngineFailed()

                    return
                }

                engine.attach({
                    container: stage,
                    labels,
                    crosshair: crosshairRef.current,
                    getSafeZone: () => contentRef.current?.getBoundingClientRect() ?? null
                })

                engine.setVisible(visibleRef.current)
                engineRef.current = engine
                homeStore.set({ engine: 'ready' })
                introEngineReady()
            })
            .catch((error: unknown) => {
                console.error('The 3D scene could not load:', error instanceof Error ? error.message : error)
                homeStore.set({ engine: 'failed' })
                introEngineFailed()
            })

        return () => {
            cancelled = true
            engineRef.current?.detach()
            engineRef.current = null
        }
    }, [])

    useEffect(() => {
        const root = rootRef.current

        if (!root) return

        const observer = new IntersectionObserver(entries => {
            const visible = entries.some(entry => entry.isIntersecting)

            visibleRef.current = visible
            engineRef.current?.setVisible(visible)
        })

        observer.observe(root)

        let raf = 0

        const update = (): void => {
            const rect = root.getBoundingClientRect()

            setBarHidden(rect.bottom < window.innerHeight * 0.7)
        }

        const onScroll = (): void => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(raf)
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [])

    return (
        <section ref={rootRef} aria-label='Live model' className='hero-root relative desk:h-svh desk:min-h-[640px] desk:overflow-hidden'>
            <div
                ref={stageRef}
                role='img'
                aria-label={`${scene.figure}. ${scene.description}`}
                className='cf-stage relative mt-14 h-[55svh] min-h-[320px] w-full touch-pan-y select-none overflow-hidden bg-background desk:absolute desk:inset-0 desk:mt-0 desk:h-auto desk:min-h-0'
            >
                <div ref={labelRef} className='cf-labels z-[1]' aria-hidden='true' />

                <div ref={crosshairRef} className='cf-crosshair z-[3]' aria-hidden='true'>
                    <i />
                    <i />
                    <i />
                    <i />
                </div>

                <SceneHud />
                <IntroCounter />

                {state.engine === 'failed' && (
                    <div className='absolute inset-0 z-[2] grid place-items-center px-gutter'>
                        <p className='hud max-w-[36ch] text-center text-mute'>
                            The live model needs WebGL, which this browser has switched off. The figures and scenes remain available below.
                        </p>
                    </div>
                )}
            </div>

            <SceneSlider />

            <div className='relative z-10 px-gutter pb-10 pt-8 desk:pointer-events-none desk:absolute desk:bottom-[72px] desk:left-gutter desk:top-16 desk:flex desk:w-[40vw] desk:min-w-[380px] desk:flex-col desk:justify-center desk:p-0'>
                <SceneCopy contentRef={contentRef} />
            </div>

            <SceneBar hidden={barHidden} />

            <p className='sr-only' aria-live='polite' aria-atomic='true'>
                {`Scene ${copy.number} of 04, ${copy.tab}. ${copy.headline.join(' ')}`}
            </p>
        </section>
    )
}
